import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Briefcase, Wrench, Star, Users, GraduationCap,
  HelpCircle, DollarSign, Inbox, Mail, Search, LogOut, Menu, X, ExternalLink,
  Image as ImageIcon, Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/portfolio", label: "Portfolio", icon: Briefcase },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/team", label: "Team", icon: Users },
  { to: "/admin/careers", label: "Careers", icon: GraduationCap },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/pricing", label: "Pricing", icon: DollarSign },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/newsletter", label: "Subscribers", icon: Mail },
  { to: "/admin/seo", label: "SEO Settings", icon: Search },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const SidebarInner = () => (
    <>
      <Link to="/admin" className="flex items-center gap-2.5 px-6 py-6">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-vantage font-heading text-base font-black text-white">W</span>
        <span className="font-heading text-base font-bold">Vantage<span className="text-vantage">.</span></span>
      </Link>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-vantage text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <Link to="/" target="_blank" className="mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:bg-white/5 hover:text-white">
          <ExternalLink className="h-4 w-4" /> View site
        </Link>
        <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {/* desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-white/10 bg-ink-900 lg:flex" data-testid="admin-sidebar">
        <SidebarInner />
      </aside>

      {/* mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-white/10 bg-ink-900">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="glass-strong sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/10 px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          <div className="hidden lg:block">
            <p className="text-sm text-slate-400">Welcome back,</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{user?.name || "Admin"}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-vantage font-heading text-sm font-bold text-white">
              {(user?.name || "A").charAt(0)}
            </span>
          </div>
        </header>
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
